import { useTranslations } from 'next-intl';
import {
  ContentLayout,
  GeneralPageContent,
} from '@silvertours/common-landingpages-view';
import {
  BenefitsLegacy,
  ContentArticle,
  SerpOffersModule,
  StageLegacy,
} from '@silvertours/front-features';
import { useRawFeatureTranslations } from '@silvertours/front-shared';
import { AcrissFilter } from '@silvertours/back-domain-offer';
import { Body } from '../body';

const GeneralContent = ({
  content: {
    article,
    attributes: { island, airport, railStation, category, bookingAttribute },
    globalRating,
    introduction,
    links,
    location,
    offersByCategory,
    stage,
    textContent: { primaryHeadline },
  },
  ...commonProps
}: ContentLayout<GeneralPageContent>) => {
  const translations = useRawFeatureTranslations('article');
  const carTypes = useRawFeatureTranslations('offers.car.type');
  const offersTranslations = useTranslations('features.offers');
  let destinationTranslation = offersTranslations('serp.title.destination');
  let defaultTitle = offersTranslations('serp.title.default');
  let searchButtonText = offersTranslations('searchButtonText');
  let serpBadge: string | undefined;
  let searchQuery = '';

  if (island) {
    destinationTranslation = offersTranslations('serp.comparisonTitle.island');
    defaultTitle = offersTranslations('serp.comparisonTitle.default');
    serpBadge = offersTranslations('serp.badge');
  }

  if (category) {
    const filterIds = category.isSportscar
      ? ['car_type_sportscar']
      : new AcrissFilter(category.query).filterIds();
    searchQuery = new URLSearchParams(
      filterIds.map(filterId => ['filter', filterId]),
    ).toString();
    destinationTranslation = offersTranslations(
      'serp.title.categoryDestination',
    ).replace('%CATEGORY%', category.name);
    defaultTitle = offersTranslations('serp.title.category').replace(
      '%CATEGORY%',
      category.name,
    );
    searchButtonText = offersTranslations('searchButtonTextCategory').replace(
      '%CATEGORY%',
      category.name,
    );
  }

  if (bookingAttribute) {
    const params = new URLSearchParams();
    bookingAttribute.forEach(attribute => {
      Object.entries(attribute.query).forEach(([key, value]) =>
        params.append(key, String(value)),
      );
      searchQuery = params.toString();
      destinationTranslation = offersTranslations(
        'serp.title.categoryDestination',
      ).replace('%CATEGORY%', attribute.name);
      defaultTitle = offersTranslations('serp.title.category').replace(
        '%CATEGORY%',
        attribute.name,
      );
      searchButtonText = offersTranslations('searchButtonTextCategory').replace(
        '%CATEGORY%',
        attribute.name,
      );
    });
  }

  if (airport) {
    destinationTranslation = offersTranslations('serp.title.airport');
    searchQuery = new URLSearchParams({
      filter: 'station_type_airport',
    }).toString();
  }

  if (railStation) {
    destinationTranslation = offersTranslations('serp.comparisonTitle.rail');
    defaultTitle = offersTranslations('serp.comparisonTitle.default');
    serpBadge = offersTranslations('serp.badge');
    searchQuery = new URLSearchParams({
      filter: 'station_type_rail',
    }).toString();
  }

  return (
    <Body
      variant={{ kind: 'default' }}
      bodyTop={
        <StageLegacy.Stage
          content={stage.content}
          headline={primaryHeadline}
          variant={{ kind: 'default' }}
        />
      }
      searchData={stage?.content?.search}
      content={({ containerModule, contentModule }) => [
        containerModule(
          'usp',
          <BenefitsLegacy.Usp rating={globalRating.content} />,
        ),
        containerModule(
          'introduction-content',
          <ContentArticle
            modules={introduction?.segments ?? []}
            translations={translations}
          />,
          !!introduction?.segments,
        ),
        contentModule(
          'serpOffers',
          !!offersByCategory.content && (
            <SerpOffersModule
              content={offersByCategory.content}
              translations={{
                airConditioning: offersTranslations('car.airConditioning'),
                carTypes,
                prevText: offersTranslations('prevText'),
                nextText: offersTranslations('nextText'),
                searchButtonText,
                ariaLabel: offersTranslations('ariaLabel'),
                disclaimertext: offersTranslations('disclaimerSerpOffers'),
                pricePrefix: offersTranslations('pricePrefix'),
                priceSuffix: offersTranslations('priceSuffix'),
                serpTitle: location.name
                  ? destinationTranslation.replace('%LOCATION%', location.name)
                  : defaultTitle,
                serpBadge,
                seats: offersTranslations('car.seats'),
                doors: offersTranslations('car.doors'),
                transmission: offersTranslations('car.transmission'),
                transmissionAutomatic: offersTranslations(
                  'car.transmissionAutomatic',
                ),
                transmissionManual: offersTranslations(
                  'car.transmissionManual',
                ),
              }}
              searchQuery={searchQuery}
              isAirport={!!airport}
              background={railStation || island ? 'mint' : undefined}
            />
          ),
          !!offersByCategory.content &&
            Object.keys(offersByCategory.content).length > 0,
        ),
        containerModule(
          'article',
          <ContentArticle
            modules={article.segments}
            translations={translations}
            searchQuery={searchQuery}
          />,
        ),
        containerModule(
          'linkLists',
          <ContentArticle
            modules={links.content ?? []}
            translations={translations}
          />,
        ),
      ]}
      {...commonProps}
    />
  );
};
export { GeneralContent };
